import React, { useEffect, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { Plus, Minus } from "lucide-react";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import ActiveFilterChips from "./ActiveFilterChips";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DashboardSearchFilters = ({
  onApply,
  apiRoutes = {
    districts: "districts",
    tehsils: "tehsils",
    ucs: "ucs",
    dehs: "dehs",
    villages: "villages",
  },
  dateFields = [],
  conditions = [
    { label: "Less Than", value: "<" },
    { label: "Greater Than", value: ">" },
    { label: "Equal To", value: "=" },
    { label: "Between", value: "between" },
  ],
  extraFilters = [],
}) => {
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [dehs, setDehs] = useState([]);
  const [villages, setVillages] = useState([]);

  const extraDefaults = extraFilters.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
    ...extraDefaults,
  });

  const toggleFilters = () => setIsFiltersVisible(!isFiltersVisible);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev, [key]: value };

      // Reset dependent filters
      if (key === "district_id") {
        updatedFilters.tehsil_id = "";
        updatedFilters.uc_id = "";
        updatedFilters.deh_id = "";
        updatedFilters.village_id = "";
        setTehsils([]);
        setUcs([]);
        setDehs([]);
        setVillages([]);
      } else if (key === "tehsil_id") {
        updatedFilters.uc_id = "";
        updatedFilters.deh_id = "";
        updatedFilters.village_id = "";
        setUcs([]);
        setDehs([]);
        setVillages([]);
      } else if (key === "uc_id") {
        updatedFilters.deh_id = "";
        updatedFilters.village_id = "";
        setDehs([]);
        setVillages([]);
      } else if (key === "deh_id") {
        updatedFilters.village_id = "";
        setVillages([]);
      }

      return updatedFilters;
    });
  };

  const fetchDropdownData = async (endpoint, params = {}) => {
    try {
      setLoading(true);
      const res = await fetchData(endpoint, "GET", null, {}, params);
      return res?.data || [];
    } catch (err) {
      console.error("Error fetching:", endpoint, err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDistricts = async () => {
      const districtData = await fetchDropdownData(apiRoutes.districts);
      setDistricts(districtData);
    };
    fetchDistricts();
  }, []);

  useEffect(() => {
    if (filters.district_id) {
      fetchDropdownData(apiRoutes.tehsils, { district_id: filters.district_id }).then(setTehsils);
    } else {
      setTehsils([]);
    }
  }, [filters.district_id]);

  useEffect(() => {
    if (filters.tehsil_id) {
      fetchDropdownData(apiRoutes.dehs, { tehsil_id: filters.tehsil_id }).then(setDehs);
    } else {
      setDehs([]);
    }
  }, [filters.tehsil_id]);

  useEffect(() => {
    if (filters.deh_id) {
      fetchDropdownData(apiRoutes.villages, { deh_id: filters.deh_id }).then(setVillages);
    } else {
      setVillages([]);
    }
  }, [filters.deh_id]);

  const [activeChips, setActiveChips] = useState([]);

  const nameOf = (list, idKey, nameKey, id) =>
    list.find((item) => String(item[idKey]) === String(id))?.[nameKey] || id;

  const buildChips = (f) => {
    const chips = [];
    extraFilters.forEach((ef) => {
      if (f[ef.key]) {
        const option = (ef.options || []).find((o) => String(o.value) === String(f[ef.key]));
        chips.push({ key: ef.key, label: ef.label, value: option?.label || f[ef.key] });
      }
    });
    if (f.district_id)
      chips.push({ key: "district_id", label: "District", value: nameOf(districts, "district_id", "district_name", f.district_id) });
    if (f.tehsil_id)
      chips.push({ key: "tehsil_id", label: "Tehsil", value: nameOf(tehsils, "tehsil_id", "tehsil_name", f.tehsil_id) });
    if (f.uc_id)
      chips.push({ key: "uc_id", label: "UC", value: nameOf(ucs, "uc_id", "uc_name", f.uc_id) });
    if (f.deh_id)
      chips.push({ key: "deh_id", label: "Deh", value: nameOf(dehs, "deh_id", "deh_name", f.deh_id) });
    if (f.village_id)
      chips.push({ key: "village_id", label: "Village", value: nameOf(villages, "village_id", "village_name", f.village_id) });

    const hasDate =
      f.dateField &&
      f.condition &&
      (f.condition === "between" ? f.startDate && f.endDate : f.dateValue);
    if (hasDate) {
      const field = dateFields
        .map((d) => (typeof d === "string" ? { value: d, label: d.replaceAll("_", " ") } : d))
        .find((d) => d.value === f.dateField);
      const condition = conditions.find((c) => c.value === f.condition)?.label || f.condition;
      chips.push({
        key: "date",
        label: field?.label || f.dateField,
        value:
          f.condition === "between"
            ? `Between ${f.startDate} – ${f.endDate}`
            : `${condition} ${f.dateValue}`,
      });
    }
    return chips;
  };

  const applyFilters = () => {
    setActiveChips(buildChips(filters));
    onApply && onApply(filters);
  };

  const CLEARS = {
    district_id: ["district_id", "tehsil_id", "uc_id", "deh_id", "village_id"],
    tehsil_id: ["tehsil_id", "uc_id", "deh_id", "village_id"],
    uc_id: ["uc_id", "deh_id", "village_id"],
    deh_id: ["deh_id", "village_id"],
    village_id: ["village_id"],
    date: ["dateField", "condition", "dateValue", "startDate", "endDate"],
  };

  const removeFilter = (key) => {
    const updated = { ...filters };
    (CLEARS[key] || [key]).forEach((k) => {
      updated[k] = "";
    });
    setActiveChips(buildChips(updated));
    setFilters(updated);
    onApply && onApply(updated);
  };

  const resetFilters = () => {
    const resetValues = {
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
      ...extraFilters.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {}),
    };
    setFilters(resetValues);
    setActiveChips([]);
    setTehsils([]);
    setUcs([]);
    setDehs([]);
    setVillages([]);
    onApply && onApply(resetValues);
  };

  return (
    <div className="my-4 w-full flex flex-col justify-between space-y-3">
      <Accordion expanded={isFiltersVisible}>
        <AccordionSummary
          expandIcon={
            isFiltersVisible ? (
              <Minus color="#ffffff" size={14} />
            ) : (
              <Plus size={14} color="#ffffff" />
            )
          }
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            backgroundColor: "#137e81",
            color: "#fff !important",
            "& .MuiAccordionSummary-content": { color: "#fff !important" },
          }}
          onClick={toggleFilters}
        >
          Filters
        </AccordionSummary>

        <AccordionDetails>
          {loading && (
            <p className="text-gray-500 text-sm mb-3">Loading data...</p>
          )}

          {/* Location Dropdowns */}
          <div className={`grid ${extraFilters.length > 0 ? "grid-cols-5" : "grid-cols-4"} gap-4 w-full`}>
            {extraFilters.map((ef) => (
              <ThinDropdownWithSearch
                key={ef.key}
                label={ef.label}
                id={`${ef.key}-dropdown`}
                options={ef.options}
                value={filters[ef.key]}
                onChange={(v) => handleFilterChange(ef.key, v)}
                required={false}
              />
            ))}

            <ThinDropdownWithSearch
              label="District"
              id="district-dropdown"
              options={districts.map((d) => ({
                value: d.district_id,
                label: d.district_name,
              }))}
              value={filters.district_id}
              onChange={(v) => handleFilterChange("district_id", v)}
              required={false}
            />

            <ThinDropdownWithSearch
              label="Tehsil"
              id="tehsil-dropdown"
              options={tehsils.map((t) => ({
                value: t.tehsil_id,
                label: t.tehsil_name,
              }))}
              value={filters.tehsil_id}
              onChange={(v) => handleFilterChange("tehsil_id", v)}
              required={false}
            />

            <ThinDropdownWithSearch
              label="Deh"
              id="deh-dropdown"
              options={dehs.map((d) => ({
                value: d.deh_id,
                label: d.deh_name,
              }))}
              value={filters.deh_id}
              onChange={(v) => handleFilterChange("deh_id", v)}
              required={false}
            />

            <ThinDropdownWithSearch
              label="Village"
              id="village-dropdown"
              options={villages.map((v) => ({
                value: v.village_id,
                label: v.village_name,
              }))}
              value={filters.village_id}
              onChange={(v) => handleFilterChange("village_id", v)}
              required={false}
            />
          </div>

          {/* Date Filters */}
          {dateFields.length > 0 ? (
            <div className={`grid ${extraFilters.length > 0 ? "grid-cols-5" : "grid-cols-4"} gap-4 w-full mt-4`}>
              <ThinDropdownWithSearch
                label="Date Field"
                id="date-field-dropdown"
                options={dateFields.map((f) =>
                  typeof f === "string"
                    ? { value: f, label: f.replaceAll("_", " ") }
                    : f
                )}
                value={filters.dateField}
                onChange={(v) => handleFilterChange("dateField", v)}
                required={false}
              />

              <ThinDropdownWithSearch
                label="Condition"
                id="condition-dropdown"
                options={conditions}
                value={filters.condition}
                onChange={(v) => handleFilterChange("condition", v)}
                required={false}
              />

              {filters.condition === "between" ? (
                <>
                  <ThinTextField
                    label="Start Date"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      handleFilterChange("startDate", e.target.value)
                    }
                    isRequired={false}
                  />
                  <ThinTextField
                    label="End Date"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      handleFilterChange("endDate", e.target.value)
                    }
                    isRequired={false}
                  />
                </>
              ) : (
                <ThinTextField
                  label="Date Value"
                  type="date"
                  value={filters.dateValue}
                  onChange={(e) =>
                    handleFilterChange("dateValue", e.target.value)
                  }
                  isRequired={false}
                />
              )}

              <div className={`flex items-end gap-2 ${extraFilters.length > 0 && filters.condition !== "between" ? "col-span-2" : ""}`}>
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#a68108] transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-[#137e81] text-white rounded-lg hover:bg-[#0f6e6f] transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#a68108] transition-colors"
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-[#137e81] text-white rounded-lg hover:bg-[#0f6e6f] transition-colors"
              >
                Apply
              </button>
            </div>
          )}
        </AccordionDetails>
      </Accordion>

      <ActiveFilterChips chips={activeChips} onRemove={removeFilter} onClearAll={resetFilters} />
    </div>
  );
};

export default DashboardSearchFilters;